import React, { useState } from 'react';
import { storage } from '../../config/firebase';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { Box, Button, Input, Typography, LinearProgress, Link } from '@mui/material';
import { useAuth } from '../../context/AuthContext';

const FileUpload = () => {
  const [file, setFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [downloadURL, setDownloadURL] = useState('');
  const [error, setError] = useState('');
  const { currentUser } = useAuth();

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      setFile(e.target.files[0]);
      setDownloadURL('');
      setError('');
      setUploadProgress(0);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first.');
      return;
    }
    if (!currentUser) {
      setError('You must be logged in to upload files.');
      return;
    }

    setIsUploading(true);
    setError('');
    setUploadProgress(0);

    // Create a storage reference (e.g., 'userFiles/userId/fileName')
    const storageRef = ref(storage, `userFiles/${currentUser.uid}/${file.name}`);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setUploadProgress(progress);
      },
      (uploadError) => {
        console.error("Upload failed:", uploadError);
        setError(`Upload failed: ${uploadError.message}`);
        setIsUploading(false);
        setUploadProgress(0);
      },
      async () => {
        try {
          const url = await getDownloadURL(uploadTask.snapshot.ref);
          setDownloadURL(url);
          setIsUploading(false);
          setFile(null); // Clear the file input after successful upload
        } catch (getUrlError) {
          console.error("Failed to get download URL:", getUrlError);
          setError(`Failed to get download URL: ${getUrlError.message}`);
          setIsUploading(false);
        }
      }
    );
  };

  return (
    <Box sx={{ p: 3, maxWidth: 500, margin: 'auto', mt: 5, backgroundColor: '#F5EFE6', borderRadius: '8px', boxShadow: '0px 4px 12px rgba(0,0,0,0.1)' }}>
      <Typography variant="h5" sx={{ mb: 2, color: '#5C3D2E', textAlign: 'center' }}>Upload File</Typography>
      <Box sx={{ mb: 2 }}>
        <Input
          type="file"
          onChange={handleFileChange}
          fullWidth
          sx={{ mb: 1 }}
          disabled={isUploading}
        />
      </Box>
      <Button
        variant="contained"
        onClick={handleUpload}
        disabled={!file || isUploading}
        fullWidth
        sx={{ backgroundColor: '#6D4C41', '&:hover': { backgroundColor: '#5D4037' } }}
      >
        {isUploading ? 'Uploading...' : 'Upload to Cloud'}
      </Button>
      {isUploading && (
        <Box sx={{ width: '100%', mt: 2 }}>
          <LinearProgress variant="determinate" value={uploadProgress} />
          <Typography variant="body2" sx={{ textAlign: 'center', mt: 0.5 }}>{Math.round(uploadProgress)}%</Typography>
        </Box>
      )}
      {error && (
        <Typography color="error" sx={{ mt: 2, textAlign: 'center' }}>
          {error}
        </Typography>
      )}
      {downloadURL && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="body1" sx={{ color: 'green' }}>Upload successful!</Typography>
          <Link href={downloadURL} target="_blank" rel="noopener noreferrer">
            View Uploaded File
          </Link>
        </Box>
      )}
    </Box>
  );
};

export default FileUpload;
